import { useState, useEffect, useMemo } from 'react';
import { supabaseAdmin } from '../../config/supabase';
import { Search, Loader2, Edit, Trash2, RefreshCw, Tag, Star, Eye } from 'lucide-react';
import { triggerKeywordGeneration } from '../../services/keywordService';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import EditCraftyForm, { Crafty as CraftyType } from '../../components/admin/EditCraftyForm';
import AdminFeatureManager from '../../components/admin/AdminFeatureManager';
import AdminBusinessPreviewModal from '../../components/admin/AdminBusinessPreviewModal';

interface Crafty extends CraftyType {
  rating?: number;
  reviews?: number;
  keywords?: string[];
  show_map?: boolean;
}

export default function Crafties() {
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crafties, setCrafties] = useState<Crafty[]>([]);
  const [professions, setProfessions] = useState<Record<number, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCrafty, setEditingCrafty] = useState<Crafty | null>(null);
  const [regeneratingKeywords, setRegeneratingKeywords] = useState<Set<number>>(new Set());
  const [featureManagerCrafty, setFeatureManagerCrafty] = useState<Crafty | null>(null);
  const [previewCrafty, setPreviewCrafty] = useState<Crafty | null>(null);

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const { data, error } = await supabaseAdmin
          .from('craft_professions')
          .select('*')
          .order('name');

        if (error) throw error;
        if (!data) throw new Error('No professions data returned');

        const profMap = data?.reduce((acc, prof) => ({
          ...acc,
          [prof.id]: prof.name
        }), {});

        setProfessions(profMap);
      } catch (err) {
        console.error('Error fetching professions:', err);
        setError('Failed to load professions');
      }
    };

    const fetchCrafties = async () => {
      try {
        const { data, error } = await supabaseAdmin
          .from('crafties')
          .select('*')
          .order('store_name');

        if (error) throw error;
        if (!data) throw new Error('No crafties data returned');

        console.log('Fetched crafties:', data);
        setCrafties(data);
      } catch (err) {
        console.error('Error fetching crafties:', err);
        setError('Failed to load crafties');
      }
    };

    Promise.all([fetchProfessions(), fetchCrafties()])
      .finally(() => setLoading(false));
  }, []);

  const filteredCrafties = useMemo(() => {
    return crafties.filter(crafty => {
      const searchLower = searchQuery.toLowerCase();
      
      // Check if any keyword matches the search query
      const keywordMatch = (crafty as any).keywords?.some((keyword: string) => 
        keyword.toLowerCase().includes(searchLower)
      ) || false;
      
      return (
        crafty.store_name.toLowerCase().includes(searchLower) ||
        crafty.owner_name.toLowerCase().includes(searchLower) ||
        crafty.location.toLowerCase().includes(searchLower) ||
        professions[crafty.profession_id]?.toLowerCase().includes(searchLower) ||
        keywordMatch
      );
    });
  }, [crafties, searchQuery, professions]);

  const handleCraftyUpdate = async (updatedCrafty: Crafty) => {
    try {
      // Verify the update was successful by fetching the latest data
      const { data, error } = await supabaseAdmin
        .from('crafties')
        .select('*')
        .eq('id', updatedCrafty.id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Failed to verify crafty update');

      setCrafties(prev => prev.map(t => t.id === data.id ? data : t));
      setEditingCrafty(null);
    } catch (err) {
      console.error('Error verifying crafty update:', err);
      setError('Failed to update crafty');
    }
  };

  const handleToggleMapVisibility = async (crafty: Crafty) => {
    const current = (crafty as any).show_map ?? true;
    const next = !current;

    try {
      const { error: updateError } = await supabaseAdmin
        .from('crafties')
        .update({ show_map: next })
        .eq('id', crafty.id);

      if (updateError) throw updateError;

      setCrafties(prev =>
        prev.map(c =>
          c.id === crafty.id
            ? { ...(c as any), show_map: next }
            : c
        )
      );

      toast({
        title: 'Success',
        description: `Map ${next ? 'enabled' : 'disabled'} for this crafty`,
      });
    } catch (error) {
      console.error('Error updating crafty map visibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to update map visibility',
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateKeywords = async (crafty: Crafty) => {
    const craftyId = Number(crafty.id);
    if (isNaN(craftyId)) return;

    setRegeneratingKeywords(prev => new Set(prev).add(craftyId));

    try {
      const success = await triggerKeywordGeneration(craftyId, 'craftie', {
        store_name: crafty.store_name,
        description: crafty.description,
        location: crafty.location,
      });

      if (success) {
        toast({
          title: 'Success',
          description: `Keyword generation started for ${crafty.store_name}`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to trigger keyword generation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error triggering keyword generation for crafty:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger keyword generation',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(craftyId);
        return newSet;
      });
    }
  };

  const handleVerifyCrafty = async (crafty: Crafty) => {
    try {
      const { error: updateError } = await supabaseAdmin
        .from('crafties')
        .update({ verified: !crafty.verified })
        .eq('id', crafty.id);

      if (updateError) throw updateError;

      setCrafties(prev =>
        prev.map(c =>
          c.id === crafty.id ? { ...c, verified: !c.verified } : c
        )
      );

      toast({
        title: 'Success',
        description: `Crafty ${crafty.verified ? 'unverified' : 'verified'} successfully`,
      });
    } catch (error) {
      console.error('Error updating crafty:', error);
      toast({
        title: 'Error',
        description: 'Failed to update crafty status',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCrafty = async (crafty: Crafty) => {
    if (!window.confirm(`Are you sure you want to delete ${crafty.store_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { data: existingCrafty, error: checkError } = await supabaseAdmin
        .from('crafties')
        .select('id, user_id, email')
        .eq('id', crafty.id)
        .single();

      if (checkError) {
        throw new Error('Failed to verify craftie record before deletion');
      }

      if (!existingCrafty) {
        setCrafties(prev => prev.filter(c => c.id !== crafty.id));
        return;
      }

      const { error: deleteCraftyError } = await supabaseAdmin
        .from('crafties')
        .delete()
        .eq('id', crafty.id);

      if (deleteCraftyError) throw deleteCraftyError;

      if (existingCrafty.user_id) {
        try {
          const { error: disableError } = await supabaseAdmin.auth.admin.updateUserById(
            existingCrafty.user_id,
            { ban_duration: '1000000h' }
          );

          if (!disableError) {
            const { error: deleteAuthError } = await supabaseAdmin.auth.admin.deleteUser(
              existingCrafty.user_id
            );

            if (deleteAuthError) {
              console.error('Error deleting craftie auth user:', deleteAuthError);
            }
          } else {
            console.error('Error disabling craftie auth user:', disableError);
          }
        } catch (authError) {
          console.error('Error managing craftie auth user during delete:', authError);
        }
      }

      setCrafties(prev => prev.filter(c => c.id !== crafty.id));
    } catch (error) {
      console.error('Error deleting crafty:', error);
    }
  };

  // Show auth loading state
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show not authorized state
  if (!adminUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Not authorized to view this page</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Crafties</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search crafties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCrafties.map((crafty) => (
            <div
              key={crafty.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <img
                    src={crafty.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${crafty.store_name}`}
                    alt={crafty.store_name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{crafty.store_name}</h3>
                    <p className="text-sm text-gray-600">{crafty.owner_name}</p>
                    <p className="text-sm text-gray-500">
                      {professions[crafty.profession_id] || 'Unknown Profession'}
                    </p>
                    <p className="text-sm text-gray-500">{crafty.location}</p>
                  </div>
                </div>

                {/* Map visibility toggle */}
                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                  <span>Map visibility</span>
                  <button
                    type="button"
                    onClick={() => handleToggleMapVisibility(crafty)}
                    className={`px-2 py-1 rounded-full border text-xs font-medium ${
                      (crafty.show_map ?? true)
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  >
                    {(crafty.show_map ?? true) ? 'On' : 'Off'}
                  </button>
                </div>

                {/* Keywords Display */}
                {crafty.keywords && crafty.keywords.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {crafty.keywords.slice(0, 5).map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                        {crafty.keywords.length > 5 && (
                          <span className="inline-block px-2 py-0.5 text-xs text-gray-500">
                            +{crafty.keywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons footer */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      crafty.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {crafty.verified ? 'Verified' : 'Pending'}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewCrafty(crafty)}
                        className="p-2 rounded-full bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors duration-200"
                        title="Quick Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* Feature Manager button */}
                      <button
                        onClick={() => setFeatureManagerCrafty(crafty)}
                        disabled={!crafty.user_id}
                        className="p-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={crafty.user_id ? 'Manage Features' : 'No user_id - cannot manage features'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      {/* Regenerate Keywords button */}
                      <button
                        onClick={() => handleRegenerateKeywords(crafty)}
                        disabled={regeneratingKeywords.has(crafty.id)}
                        className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate Keywords"
                      >
                        <RefreshCw className={`w-4 h-4 ${regeneratingKeywords.has(crafty.id) ? 'animate-spin' : ''}`} />
                      </button>
                      {/* Verify/Unverify button */}
                      <button
                        onClick={() => handleVerifyCrafty(crafty)}
                        className={`p-2 rounded-full transition-colors duration-200 ${
                          crafty.verified
                            ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                        title={crafty.verified ? 'Unverify Crafty' : 'Verify Crafty'}
                      >
                        {crafty.verified ? '✓' : '○'}
                      </button>
                      {/* Edit button */}
                      <button
                        onClick={() => setEditingCrafty(crafty)}
                        className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors duration-200"
                        title="Edit Crafty"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {/* Delete button */}
                      <button
                        onClick={() => handleDeleteCrafty(crafty)}
                        className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors duration-200"
                        title="Delete Crafty"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Crafty Form Modal */}
      {editingCrafty && (
        <EditCraftyForm
          crafty={editingCrafty}
          onClose={() => setEditingCrafty(null)}
          onSave={handleCraftyUpdate}
        />
      )}

      {/* Feature Manager Modal */}
      {featureManagerCrafty && featureManagerCrafty.user_id && (
        <AdminFeatureManager
          userId={featureManagerCrafty.user_id}
          businessName={featureManagerCrafty.store_name}
          onClose={() => setFeatureManagerCrafty(null)}
          onSave={() => {
            toast({
              title: 'Success',
              description: `Features updated for ${featureManagerCrafty.store_name}`,
            });
          }}
        />
      )}

      {previewCrafty && (
        <AdminBusinessPreviewModal
          businessType="crafty"
          business={previewCrafty}
          contextLabel={professions[previewCrafty.profession_id]}
          onClose={() => setPreviewCrafty(null)}
        />
      )}
    </div>
  );
}
